import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, ArrowLeft, KeyRound, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Footer } from "@/components/Footer";
import axios from "axios";
import { API } from "@/App";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [step, setStep] = useState(1); // 1 = email, 2 = enter token + new password

  // Password strength matching backend requirements
  const passwordStrength = {
    minLength: newPassword.length >= 8,
    hasUppercase: /[A-Z]/.test(newPassword),
    hasLowercase: /[a-z]/.test(newPassword),
    hasNumber: /\d/.test(newPassword),
  };
  const passwordValid = Object.values(passwordStrength).every(Boolean);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email");
      return;
    }

    setLoading(true);
    try {
      // Backend always returns 200 — token is emailed to user if email exists
      await axios.post(`${API}/forgot-password`, { email });
      setStep(2);
      toast.success("Check your email! If registered, a reset token has been sent.");
    } catch (error) {
      // Shouldn't 404 anymore, but handle gracefully
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetToken.trim()) {
      toast.error("Please enter the reset token from your email");
      return;
    }
    if (!passwordValid) {
      toast.error("Password must be 8+ chars with uppercase, lowercase, and a number");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/reset-password`, { 
        email,
        reset_token: resetToken.trim().toUpperCase(),
        new_password: newPassword
      });
      setSubmitted(true);
      toast.success("Password reset successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid or expired token. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian flex flex-col">
      {/* Header */}
      <header className="bg-charcoal/50 border-b border-slate-800 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <span className="font-display text-xl text-gold-gradient">ExpertBase</span>
          </Link>
          <Link 
            to="/login" 
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Login
          </Link>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center p-6">
        <motion.div 
          className="w-full max-w-md"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gold-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <KeyRound className="w-8 h-8 text-gold-500" />
            </div>
            <h1 className="font-display text-3xl mb-2">Reset Password</h1>
            <p className="text-slate-400">
              {submitted 
                ? "Your password has been reset successfully" 
                : step === 1 
                  ? "Enter your email to receive a reset token"
                  : "Enter your token and choose a new password"
              }
            </p>
          </div>

          <div className="bg-charcoal border border-slate-800 p-8 rounded-sm">
            {submitted ? (
              <div className="text-center">
                <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <p className="text-slate-300 mb-6">Your password has been successfully reset.</p>
                <Link to="/login">
                  <Button className="w-full bg-gold-500 hover:bg-gold-400 text-black font-semibold py-6 rounded-sm">
                    Go to Login
                  </Button>
                </Link>
              </div>
            ) : step === 1 ? (
              <form onSubmit={handleRequestReset} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-12 bg-obsidian border-slate-700 focus:border-gold-500 h-12"
                      data-testid="forgot-email"
                    />
                  </div>
                </div>

                <Button 
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gold-500 hover:bg-gold-400 text-black font-semibold py-6 rounded-sm"
                  data-testid="forgot-submit"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  ) : (
                    "Send Reset Token"
                  )}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleResetPassword} className="space-y-5">
                {/* Info banner */}
                <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-sm">
                  <p className="text-blue-300 text-sm">
                    📧 A reset token has been sent to <strong>{email}</strong> if that address is registered.
                    Check your inbox (and spam folder) and enter the token below.
                  </p>
                </div>

                {/* Token input */}
                <div className="space-y-2">
                  <Label htmlFor="resetToken">Reset Token (from email)</Label>
                  <Input
                    id="resetToken"
                    type="text"
                    placeholder="e.g. A3BF92C1"
                    value={resetToken}
                    onChange={(e) => setResetToken(e.target.value.toUpperCase())}
                    className="bg-obsidian border-slate-700 focus:border-gold-500 h-12 font-mono tracking-widest text-center text-lg uppercase"
                    maxLength={8}
                    data-testid="reset-token-input"
                  />
                  <p className="text-xs text-slate-500">Enter the 8-character token from your email</p>
                </div>

                {/* New password */}
                <div className="space-y-2">
                  <Label htmlFor="newPassword">New Password</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    placeholder="Create a strong password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="bg-obsidian border-slate-700 focus:border-gold-500 h-12"
                    data-testid="new-password"
                  />
                  {/* Password strength checklist */}
                  {newPassword && (
                    <div className="grid grid-cols-2 gap-1 mt-2">
                      {[
                        { ok: passwordStrength.minLength, label: "8+ characters" },
                        { ok: passwordStrength.hasUppercase, label: "Uppercase letter" },
                        { ok: passwordStrength.hasLowercase, label: "Lowercase letter" },
                        { ok: passwordStrength.hasNumber, label: "Number" },
                      ].map(({ ok, label }) => (
                        <div key={label} className={`flex items-center gap-1 text-xs ${ok ? "text-green-400" : "text-slate-500"}`}>
                          <span>{ok ? "✓" : "○"}</span>
                          <span>{label}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Confirm password */}
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="Repeat your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={`bg-obsidian border-slate-700 focus:border-gold-500 h-12 ${
                      confirmPassword && newPassword !== confirmPassword ? "border-red-500" : ""
                    }`}
                    data-testid="confirm-password"
                  />
                  {confirmPassword && newPassword !== confirmPassword && (
                    <p className="text-xs text-red-400">Passwords do not match</p>
                  )}
                </div>

                <Button 
                  type="submit"
                  disabled={loading || !passwordValid || newPassword !== confirmPassword}
                  className="w-full bg-gold-500 hover:bg-gold-400 text-black font-semibold py-6 rounded-sm disabled:opacity-50"
                  data-testid="reset-submit"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  ) : (
                    "Reset Password"
                  )}
                </Button>

                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="w-full text-center text-sm text-slate-400 hover:text-white transition-colors"
                >
                  ← Use a different email
                </button>
              </form>
            )}
          </div>

          <p className="text-center text-slate-400 mt-6">
            Remember your password?{" "}
            <Link to="/login" className="text-gold-500 hover:text-gold-400 transition-colors">
              Sign in
            </Link>
          </p>
        </motion.div>
      </div>
      
      <Footer />
    </div>
  );
}
